import { useState } from "react";

import {
  Box,
  Button,
  Divider,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { useAvailableViewportHeight, useShipmentFiltersContext } from "hooks";
import { getActiveFiltersCount } from "utils/shipment";

import { CompactShipmentPreview } from "../ShipmentPreview";

// Placeholder for the brief instant before the real height is measured.
const FALLBACK_HEIGHT = "calc(100vh - 300px)";

interface Props {
  shipments: ShipperCondensedShipment[];
  shipmentSelectionIsDisabled: boolean;
  handlePageSelected: (e: React.ChangeEvent<unknown>, value: number) => void;
  page: number;
  pages: number;
}

export function QuickShipmentsBrowser(props: Props) {
  const params = useParams();
  const shipmentId = params.id;
  const {
    shipments,
    shipmentSelectionIsDisabled,
    handlePageSelected,
    page,
    pages,
  } = props;
  const [selected, setSelected] = useState<number | undefined>(
    shipmentId ? +shipmentId : undefined,
  );
  const { filters, clearFilters } = useShipmentFiltersContext("regular");
  const activeFiltersCount = getActiveFiltersCount(filters);
  const { ref, availableHeight } = useAvailableViewportHeight();
  const height =
    availableHeight !== null ? `${availableHeight}px` : FALLBACK_HEIGHT;

  if (shipments.length === 0 && activeFiltersCount > 0) {
    return (
      <Box ref={ref} display="flex" flexDirection="column" height={height}>
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
          sx={{ flex: 1, p: 2, pt: 6 }}
        >
          <Typography sx={{ textAlign: "center" }} variant="body1">
            No shipments found with current filters.
          </Typography>
          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box ref={ref} display="flex" flexDirection="column" height={height}>
      <Box sx={{ flex: 1, overflowY: "scroll", overflowX: "hidden" }}>
        {shipments.map((s, i) => (
          <Box key={s.info.id}>
            {i > 0 && <Divider />}
            <CompactShipmentPreview
              shipment={s}
              selected={s.info.id === selected}
              select={setSelected}
              selectionIsDisabled={shipmentSelectionIsDisabled}
            />
          </Box>
        ))}
      </Box>
      <Divider />
      <Box my="1rem" display="flex" alignItems="center" justifyContent="center">
        <Pagination count={pages} onChange={handlePageSelected} page={page} />
      </Box>
    </Box>
  );
}
